import { useMutation, useQuery } from '@apollo/client';
import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import { AttachmentItemHolder } from '@nile/banyan/src/Components/Editors/RTEditor';

import {
  ALL_MODULES,
  AppContext,
  BackgroundIcon,
  ContentDeciderContainer,
  EventEmitter,
  fetchValues,
  getAsyncOptions,
  getDefaultValue,
  getDownloadURL,
  getHeaderValue,
  getObjectValueCount,
  getTranslatedText,
  Loader,
  Sheet,
  SkeletonTableLoading,
  T1,
  TableContainer,
  toast,
  updateConditions,
  DateTimeFormatter,
  useUserMSPCheckHook,
  useIsAuthorisedHook,
  SideConversations,
  NewSideConversation,
  TabsContainer,
  isAuthorised,
  sideConversationPricingPlans,
  TabContent,
  EmptyTabState,
  useCheckFeatureGating,
  fetchApi,
  getConversationOrder,
  scrollToTop,
  scrollToBottom,
  COLOR_MAP
} from '@nile/components';
import MultipleNamesHandler from '@nile/components/src/Pebbles/multipleNamesHandler';
import {
  AccountClient,
  CREATE_CLIENT_CONVERSATION,
  GET_CLIENT_CONVERSATIONS,
  GET_CLIENT_USERS,
  UPDATE_READ_STATUS,
  GET_USER_PROFILE,
  useCachedQuery,
  GET_VENDOR_CONVERSATION_LIST,
  MockTicketClient,
  GET_NOTE_LIST
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import Recepients from '@nile/mangifera/src/Components/Chat/Recepients';
import { DateTimeCell } from '@nile/mangifera/src/Components/Ticket';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { Receiver, Sender } from '@nile/mangifera/src/Pebbles/chat';
import { SelectUsers } from '@nile/mangifera/src/Pebbles/ChatContainer';
import {
  TextStyle,
  Button,
  Link,
  Avatar,
  DisplayText,
  Card,
  ButtonGroup
} from '@superops/ui-design-system';
import dayjs from 'dayjs';
import dompurify from 'dompurify';
import Collapse from 'react-collapse';
import { EditorState } from 'draft-js';
import { cloneDeep, isEmpty, pick } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { Tooltip } from 'react-clone-tippy';
import Tippy from '@tippyjs/react';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { NotesContainer } from '@nile/mangifera/src/Containers/ChatContainers';
import EmailFailureStatus from '@nile/mangifera/src/Components/Chat/EmailFailureState';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import {
  ThreadWrapper,
  Container,
  Wrapper,
  ReplyThreadWrapper,
  EditorHolder,
  ItemHolders,
  AttachmentsContainer,
  CopyMailUsers,
  getUserName,
  findLastIndex,
  getCCUsersToCarryForward,
  getCCUsers,
  getBccUsers,
  ConversationThreadHeader,
  ConversationBubble,
  ReplyWidget,
  ComposeSection,
  HiddenActions
} from '@nile/client_management/src/Components/Conversations/CommonConversations';

const InvoiceConversations = (props) => {
  const {
    conversationIds,
    clientId,
    intl,
    conversationInfo,
    invoiceId
  } = props;
  const flags = useFlags();
  const appContext = useContext(AppContext);
  const isMSP = useUserMSPCheckHook();
  const isConversationViewAuthorised = useIsAuthorisedHook(
    'clientConversation.view'
  );
  const userRole = appContext?.user?.role?.roleFeatureList || [];
  const isEditAuthorised = useIsAuthorisedHook('invoice.edit');
  const currentUser = appContext?.user || {};
  const planId = currentUser?.chargeBeePlanId?.toLowerCase();
  const [conversations, setConversations] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const switchTabIndex = (idx) => {
    setActiveTabIndex(idx);
  };
  const showSideConversation = useCheckFeatureGating('SIDE_CONVERSATIONS')
    ?.enabled;
  const [sideConversationToBeOpened, setSideConversationToBeOpened] = useState(
    null
  );

  // value from user preferred conversation order
  const conversationOrder = getConversationOrder() || 'ASC';

  const {
    loading: allConversationsLoading,
    data: allConversations,
    error: allConversationError,
    refetch
  } = useQuery(GET_CLIENT_CONVERSATIONS, {
    skip: !isConversationViewAuthorised,
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      isConversation: true,
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              joinOperator: 'OR',
              operands: [
                {
                  attribute: 'conversationId',
                  operator: 'includes',
                  value: conversationIds
                }
              ]
            },
            {
              attribute: 'client.accountId',
              operator: 'is',
              value: clientId
            }
          ]
        },

        sort: [
          {
            attribute: 'time',
            order: conversationOrder
          }
        ]
      }
    },
    onCompleted({ getClientConversationList }) {
      setConversations(getClientConversationList?.conversations || []);
    }
  });
  const {
    data: sideConversationData,
    loading: sideConversationLoading,
    error: sideConversationError,
    refetch: sideConversationRefetch
  } = useQuery(GET_VENDOR_CONVERSATION_LIST, {
    client: AccountClient,
    variables: {
      input: {
        module: 'INVOICE',
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'parentConversationId',
                    operator: 'isEmpty'
                  }
                ]
              },
              {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'workItem.workId',
                    operator: 'is',
                    value: invoiceId
                  }
                ]
              }
            ]
          },
          sort: [
            {
              attribute: 'time',
              order: conversationOrder
            }
          ]
        }
      }
    }
  });
  const { data: notesList, loading: notesListLoading } = useQuery(
    GET_NOTE_LIST,
    {
      skip: !isMSP,
      fetchPolicy: 'cache-and-network',
      variables: {
        input: {
          workItem: {
            workId: invoiceId,
            module: 'INVOICE'
          }
        }
      }
    }
  );
  const emailSignature = currentUser?.emailSignature;

  useEffect(() => {
    if (isConversationViewAuthorised)
      refetch().then((res) => {
        if (res?.data?.getClientConversationList) {
          setConversations(
            res?.data?.getClientConversationList?.conversations || []
          );
        }
      });
  }, [conversationIds]);

  useEffect(() => {
    let vendorSideConversation =
      sideConversationData?.getVendorConversationsList?.conversations || [];
    if (vendorSideConversation?.length > 0) {
      if (conversationOrder === 'ASC') {
        //set conversationId in state to open latest conversation
        let openedConversationId =
          vendorSideConversation[vendorSideConversation.length - 1]
            ?.conversationId;
        openedConversationId &&
          setSideConversationToBeOpened(openedConversationId);
      }
    }
  }, [sideConversationData]);

  const createNewSideConversation = () => {
    function onSubmit(data) {
      // refetchSideConversation();
      // switchToConnect(data);
      sideConversationRefetch();
    }
    return (
      <NewSideConversation
        plain
        intl={intl}
        workItemId={invoiceId}
        module="INVOICE"
        updateCache={() => {}}
        onSubmit={onSubmit}
        backgroundThreadConversation={flags?.sideConversationBackgroundThread}
      />
    );
  };
  if (allConversationsLoading) return <Loader />;
  const tabs = [
    {
      id: 'conversations',
      content: (
        <TabContent
          content={`${getTranslatedText(intl, 'quote.details.conversations')}`}
          // icon={<SVGIcon name="helpcenter" />}
          count={conversations.length || 0}
        />
      ),
      iconName: 'Conversation',
      panelID: 'accepts-tasks-content',
      authorisation: 'clientConversation.view',
      // component: <TaskContainer ticket={ticket} />
      component: conversations.length ? (
        <>
          {conversations.map((conversation, idx) => {
            return (
              <>
                <ChatThreads
                  {...props}
                  conversation={conversation}
                  isOpened={
                    conversation?.conversationId ===
                    conversationIds[conversationIds?.length - 1]
                  }
                  emailSignature={emailSignature}
                />
              </>
            );
          })}
        </>
      ) : (
        <Container border={props.outsideSheet} className="full-height">
          <EmptyTabState
            iconName="EmptyTabConversation"
            title="invoice.conversations.emptyState.title"
            description="invoice.conversations.emptyState.description"
          />
        </Container>
      )
    },
    {
      id: 'notes',
      content: (
        <TabContent
          content={`${getTranslatedText(intl, 'invoice.details.notes')}`}
          // icon={<SVGIcon name="helpcenter" />}
          count={notesList?.getNoteList?.length || 0}
        />
      ),
      iconName: 'Notes',
      panelID: 'invoice-notes',
      // authorisation: 'invoice.edit',
      allowOnlyMSP: true,
      component: (
        <Container border={props.outsideSheet} className="full-height">
          <NotesContainer
            module="INVOICE"
            workItemId={invoiceId}
            placeholder="quote.details.notes.placeHolder.desc"
            disableEdit={!useIsAuthorisedHook('invoice.edit')}
          />
        </Container>
      )
    },
    {
      id: 'connect',
      content: (
        <TabContent
          content={`${getTranslatedText(
            intl,
            'app.common.sideConversation.title'
          )}`}
          // icon={<SVGIcon name="helpcenter" />}
          count={
            sideConversationData?.getVendorConversationsList?.conversations
              .length || 0
          }
        />
      ),
      iconName: 'Helpcenter',
      panelID: 'connect',
      // authorisation: 'invoice.edit',
      allowOnlyMSP: true,
      pricingPlans: sideConversationPricingPlans,
      featureGateName: 'SIDE_CONVERSATIONS',
      // component: <TaskContainer ticket={ticket} />
      component: (
        <Container border={props.outsideSheet} className="full-height">
          <SideConversations
            module="INVOICE"
            workItemId={invoiceId}
            refetch={sideConversationRefetch}
            updateFeedsCache={() => {}}
            createNewSideConversation={
              showSideConversation &&
              isEditAuthorised &&
              createNewSideConversation
            }
            vendorConversations={
              sideConversationData?.getVendorConversationsList?.conversations
            }
            conversationToBeOpened={sideConversationToBeOpened}
            emptyStateData={{
              description:
                'invoice.details.sideConversation.emptyState.description'
            }}
            backgroundThreadConversation={
              flags?.sideConversationBackgroundThread
            }
          />
        </Container>
      )
    }
  ].filter(({ pricingPlans, featureGateName }) => {
    if (featureGateName) {
      return useCheckFeatureGating(featureGateName)?.enabled;
    }
    return true;
  });

  const allowedTabs = tabs.filter((el) => {
    if (!el.allowOnlyMSP) return true;
    return useUserMSPCheckHook();
  });

  let authorisedTabs = allowedTabs.filter((el) => {
    if (!el.authorisation) return true;
    const isFeatureAuthorised = isAuthorised(userRole, `${el.authorisation}`);
    return isFeatureAuthorised;
  });

  if (currentUser.accountType !== 'MSP') {
    authorisedTabs = authorisedTabs.filter((el) => el.id !== 'connect');
  }

  if (authorisedTabs?.length)
    return (
      <div className="fh common-conversation-tabs">
        <TabsContainer
          onTabChange={switchTabIndex}
          activeTabIndex={activeTabIndex}
          tabs={authorisedTabs}
          wrapperClassNames="ghost-white"
        />
      </div>
    );
  return null;
  // return (
  //   <>
  //     {conversations.map((conversation, idx) => {
  //       return (
  //         <>
  //           <ChatThreads
  //             {...props}
  //             conversation={conversation}
  //             isOpened={idx === conversationIds?.length - 1}
  //             emailSignature={emailSignature}
  //           />
  //         </>
  //       );
  //     })}
  //   </>
  // );
};

function ChatThreads(props) {
  const {
    isOpened,
    conversation,
    clientId,
    intl,
    emailSignature,
    conversationInfo
  } = props;
  const [isOpen, setIsOpen] = useState(isOpened || false);
  const toggleThread = useCallback(() => {
    setIsOpen(!isOpen);
  });

  return (
    <ThreadWrapper isOpen={isOpen}>
      <ConversationThreadHeader
        conversation={conversation}
        isOpen={isOpen}
        toggleThread={toggleThread}
        getUserName={getUserName}
        intl={intl}
      />
      <Collapse isOpened={isOpen}>
        <ChatThreadContainer
          currentReplyThread={conversation}
          clientId={clientId}
          intl={intl}
          emailSignature={emailSignature}
          conversationInfo={conversationInfo}
        />
      </Collapse>
    </ThreadWrapper>
  );
}

function ChatThreadContainer(props) {
  const flags = useFlags();

  const domain = window.location.host;
  const {
    currentReplyThread,
    clientId,
    intl,
    parentConversationIdFromParams,
    conversationIdFromParams,
    setSelectedConversation,
    emailSignature,
    conversationInfo
  } = props;
  const [conversations, setConversations] = useState([]);
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const isMSP = useUserMSPCheckHook();
  const { company } = appContext;
  const [createClientConversation] = useMutation(CREATE_CLIENT_CONVERSATION, {
    client: AccountClient
  });
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);
  const [replyThreadOpen, setReplyThreadOpen] = useState(false);

  const toggleThreadReply = () => {
    setReplyThreadOpen(!replyThreadOpen);
  };

  const [state, setState] = useState({
    editorState: EditorState.createEmpty(),
    template: {
      name: '',
      content: emailSignature ? `<p><br></p><p><br></p>${emailSignature}` : '',
      placeholdersUsed: '',
      attachments: [],
      inlineImages: []
    },
    toUsers: [],
    content: '',
    parentConversationId:
      currentReplyThread?.parentConversation?.conversationId ||
      currentReplyThread?.conversationId ||
      parentConversationIdFromParams
  });

  const [ccUsers, setCCUsers] = useState(null);
  const [bccUsers, setBCCUsers] = useState(null);
  const [updateReadStatus] = useMutation(UPDATE_READ_STATUS, {
    client: AccountClient
  });
  const isCreateAuthorised = useIsAuthorisedHook('clientConversation.add');

  // value from user preferred conversation order
  const conversationOrder = getConversationOrder() || 'ASC';

  const invoiceConversationdRef = useRef();
  /**
   * Take the cc users from the latest reply
   * It can be either technician or requester's reply
   * If a person in cc replies via email, then we should include that
   * person in cc automatically if that user is not a requester/ a custom email.
   * Also, add to cc if there are cc when a ticket is created from email and if cc is appended there.
   * The type is 'DESCRIPTION' for that case.
   */

  const findLastIndex = (array = [], keyToCompare, keys) => {
    for (let i = array.length - 1; i >= 0; i--) {
      if (array[i]?.user?.accountType === 'MSP') return i;
    }
    return -1;
  };

  const getCCUsersToCarryForward = (convos) => {
    const replyIndex = findLastIndex(convos);
    let allCCUsers = [];
    if (replyIndex > -1) {
      const users = convos[replyIndex]?.ccUsers || [];
      allCCUsers = users.map((el) => {
        return el.user;
      });
      return allCCUsers;
    }
    return [];
  };

  useEffect(() => {
    if (conversations.length > 0) {
      if (conversationOrder === 'ASC') {
        scrollToBottom(invoiceConversationdRef);
      } else {
        scrollToTop(invoiceConversationdRef);
      }
    }
  }, [conversations]);

  useEffect(() => {
    setState({
      ...state,
      parentConversationId:
        currentReplyThread?.parentConversation?.conversationId ||
        currentReplyThread?.conversationId ||
        parentConversationIdFromParams
    });
    if (currentReplyThread && !currentReplyThread.opened) {
      updateReadStatus({
        variables: {
          conversationId: currentReplyThread?.conversationId,
          status: true
        }
      });
    }
  }, [currentReplyThread]);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const Tags = {
    sender: Sender,
    receiver: Receiver
  };
  const loggedInUser = appContext.user;
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setState({
      editorState: EditorState.createEmpty(),
      template: {
        name: '',
        content: emailSignature
          ? `<p><br></p><p><br></p>${emailSignature}`
          : '',
        placeholdersUsed: '',
        attachments: [],
        inlineImages: []
      },
      toUsers: [],
      content: '',
      parentConversationId:
        currentReplyThread?.parentConversation?.conversationId ||
        currentReplyThread?.conversationId ||
        parentConversationIdFromParams
    });
    setShowCC(false);
    setShowBCC(false);
    setCCUsers(null);
    setBCCUsers(null);
  }, [clientId]);
  let [formatterAttachment, setFormatterAttachment] = useState([]);
  const {
    loading: allConversationsLoading,
    data: allConversations,
    error: allConversationError,
    refetch
  } = useQuery(GET_CLIENT_CONVERSATIONS, {
    skip:
      !currentReplyThread &&
      !conversationIdFromParams &&
      !parentConversationIdFromParams,
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      isConversation: true,
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              joinOperator: 'OR',
              operands: [
                {
                  attribute: 'parentConversationId',
                  operator: 'is',
                  value:
                    currentReplyThread?.parentConversation?.conversationId ||
                    currentReplyThread?.conversationId ||
                    parentConversationIdFromParams
                },
                {
                  attribute: 'conversationId',
                  operator: 'is',
                  value:
                    currentReplyThread?.parentConversation?.conversationId ||
                    currentReplyThread?.conversationId ||
                    conversationIdFromParams
                }
              ]
            },
            { attribute: 'client.accountId', operator: 'is', value: clientId }
          ]
        },

        sort: [
          {
            attribute: 'time',
            order: conversationOrder
          }
        ]
      }
    },
    onCompleted({ getClientConversationList }) {
      setConversations(getClientConversationList?.conversations || []);
      const allCCUsers = getCCUsersToCarryForward(
        getClientConversationList?.conversations || []
      );
      setCCUsers(
        allCCUsers.map((el) => {
          return {
            ...el,
            value: el.email,
            label: el.name || el.email
          };
        })
      );
      if (allCCUsers.length) setShowCC(true);
    }
  });
  const updateTemplate = (obj) => {
    const newState = cloneDeep(state);
    newState.template = { ...state.template, ...obj };
    setState(newState);
  };

  const handleFileAttachments = (files) => {
    const formData = new FormData();
    // formData.append('files', files);
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });
    const details = {
      clientId: clientId
    };
    if (clientId) {
      formData.append('details', JSON.stringify(details));
    }
    formData.append('module', 'CLIENT_CONVERSATION_ATTACHMENT');
    // if (template?.id) {
    //   formData.append('notificationTemplateId', template.id);
    // }
    // for (const property in uploadParams) {
    //   formData.append(property, uploadParams[property]);
    // }

    console.log('FILES are ', files);

    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };
    fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
      .then((response) => {
        console.log('email template attachment response is ', response);
        return response.json();
      })
      .then((list) => {
        const newState = cloneDeep(state);
        const updatedTemplate = newState.template;
        const updatedList = [...updatedTemplate?.attachments, ...list.data];
        updatedTemplate.attachments = updatedList.map((li) => ({
          ...pick(li, ['fileName', 'fileSize', 'originalFileName'])
        }));
        setState(newState);
      })
      .catch((err) => {
        console.error('Error:', err);
      });
  };

  const handleAttachmentsDeletion = (fileName) => {
    const newState = cloneDeep(state);
    const updatedTemplate = newState.template;
    const updatedList = cloneDeep(updatedTemplate?.attachments).filter(
      (item) => item.fileName !== fileName
    );
    updatedTemplate.attachments = updatedList;
    setState(newState);
  };

  const getToUsers = () => {
    if (!isMSP) {
      return null;
    }
    const { user } = appContext;
    let toUsers = null;
    const firstConversation = conversations?.[0];
    if (!firstConversation?.user) {
      // system generated mails
      toUsers = firstConversation?.toUsers
        ? firstConversation.toUsers.map((el) => {
            return {
              email: el.user?.email
            };
          })
        : [];
    } else if (firstConversation?.user?.accountType === 'MSP') {
      // tech replies
      toUsers = firstConversation?.toUsers
        ? firstConversation.toUsers.map((el) => {
            return {
              email: el.user?.email
            };
          })
        : [];
    } else if (firstConversation?.user?.email) {
      // requester initiated conversation
      toUsers = [{ email: firstConversation.user.email }];
    }
    return toUsers;
  };

  const composeEmail = (mail, callback) => {
    setLoading(true);
    createClientConversation({
      variables: {
        clientId: props.clientId,
        ...mail
      },
      input: {
        clientId: props.clientId,
        ...mail
      }
    })
      .then((res) => {
        setLoading(false);
        refetch().then((updatedData) => {
          setConversations(
            updatedData?.data?.getClientConversationList?.conversations || []
          );
        });
        if (res?.data?.createClientConversation) {
          toast('success', 'Mail composed successfully');
          const newState = cloneDeep(state);
          newState.template = {
            ...state.template,
            name: '',
            content: emailSignature
              ? `<p><br></p><p><br></p>${emailSignature}`
              : '',
            placeholdersUsed: '',
            attachments: [],
            inlineImages: []
          };
          setState(newState);
          callback &&
            callback(
              updatedData?.data?.getClientConversationList?.conversations
            );
        } else {
          // toast('error', 'Something went wrong');
        }
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const getCCUsers = (users, toMap) => {
    return users.filter((el) => !toMap[el.email]);
  };

  const getBccUsers = (users, toMap, ccMap) => {
    return users.filter((el) => !toMap[el.email] && !ccMap[el.email]);
  };

  const resetState = (convos) => {
    setState({
      editorState: EditorState.createEmpty(),
      template: {
        name: '',
        content: emailSignature
          ? `<p><br></p><p><br></p>${emailSignature}`
          : '',
        placeholdersUsed: '',
        attachments: [],
        inlineImages: []
      },
      toUsers: [],
      content: '',
      parentConversationId:
        currentReplyThread?.parentConversation?.conversationId ||
        currentReplyThread?.conversationId
    });
    setShowCC(false);
    setShowBCC(false);
    setCCUsers(getCCUsersToCarryForward(convos));
    setBCCUsers(null);
  };

  const composeMail = () => {
    // const rawContent = draftToHtml(
    //   convertToRaw(state.editorState.getCurrentContent())
    // );
    const rawContent = dompurifier(state?.template?.content) || '';
    setLoading(true);
    const allToUsers = getToUsers();
    const allCcUsers = ccUsers?.length
      ? ccUsers.map((el) => {
          return { email: el.email || el.value };
        })
      : null;
    const allBccUsers = bccUsers?.length
      ? bccUsers.map((el) => {
          return { email: el.email };
        })
      : null;
    const toUserMap = getObjectValueCount(allToUsers || [], 'email');
    const ccUserMap = getObjectValueCount(allCcUsers || [], 'email');
    const possibleCCusers = getCCUsers(allCcUsers || [], toUserMap);
    const possibleBccUsers = getBccUsers(
      allBccUsers || [],
      toUserMap,
      ccUserMap
    );
    composeEmail(
      {
        ...state,
        toUsers: allToUsers
          ? allToUsers.map((el) => {
              return {
                userId: el.userId,
                email: el.email
              };
            })
          : null,
        ccUsers: possibleCCusers,
        bccUsers: possibleBccUsers,
        content: rawContent
      },
      resetState
    );
  };

  const updateCCState = (sec, val) => {
    switch (sec) {
      case 'cc':
        setShowCC(val);
        break;
      case 'bcc':
        setShowBCC(val);
        break;
      default:
        break;
    }
  };
  const allToUsers = getToUsers();
  return (
    <Wrapper className="w100 bg-white">
      <ReplyThreadWrapper className="full-height flex col">
        {allConversationsLoading ? (
          <Loader />
        ) : (
          <>
            <div
              className="overflow-auto fg1 p10"
              ref={invoiceConversationdRef}
            >
              {conversations.map((conversation) => {
                const userType =
                  conversation?.user?.userId === loggedInUser?.userId
                    ? 'sender'
                    : 'receiver';
                const isConversationViewed = conversationInfo?.find(
                  (el) =>
                    el?.conversationId === conversation?.conversationId &&
                    el?.viewedByClient
                );

                return (
                  <ConversationBubble
                    key={conversation.conversationId}
                    conversation={conversation}
                    userType={userType}
                    loggedInUser={loggedInUser}
                    getUserName={getUserName}
                    company={company}
                    intl={intl}
                    clientId={clientId}
                    isConversationViewed={isConversationViewed}
                  >
                    <p
                      className="message-content"
                      dangerouslySetInnerHTML={{
                        __html: dompurifier(conversation.content)
                      }}
                    />
                    {conversation?.minifiedContent && (
                      <a
                        href={`https://${domain}/#/minify/invoice/clientconversation/${clientId}/${conversation?.conversationId}`}
                        target="_blank"
                      >
                        View All
                      </a>
                    )}
                    <div className="email-status">
                      <EmailFailureStatus
                        toUsers={currentReplyThread?.toUsers}
                        ccUsers={currentReplyThread?.ccUsers}
                        bccUsers={currentReplyThread?.bccUsers}
                      />
                    </div>
                  </ConversationBubble>
                );
              })}
            </div>

            {!replyThreadOpen && (
              <ReplyWidget toggleThreadReply={toggleThreadReply} />
            )}

            {replyThreadOpen && (
              <ComposeSection
                isMSP={isMSP}
                allToUsers={allToUsers}
                updateCCState={updateCCState}
                showCC={showCC}
                showBCC={showBCC}
                ccUsers={ccUsers}
                setCCUsers={setCCUsers}
                bccUsers={bccUsers}
                setBCCUsers={setBCCUsers}
                clientId={clientId}
                intl={intl}
                state={state}
                updateTemplate={updateTemplate}
                handleFileAttachments={handleFileAttachments}
                handleAttachmentsDeletion={handleAttachmentsDeletion}
                toggleThreadReply={toggleThreadReply}
                loading={loading}
                isCreateAuthorised={isCreateAuthorised}
                composeMail={composeMail}
              />
            )}
          </>
        )}
      </ReplyThreadWrapper>
    </Wrapper>
  );
}

export default injectIntl(InvoiceConversations);
